/**
 * Customer Detail Page — /office/customers/<pk>/?tab=…
 *
 * Wires the collapsible "Filters" panel exposed by the Worksites tab
 * (and any future tab partial that opts into the same shell, see
 * ``office-detail-tab-tables.mdc``).
 *
 * Form submit is handled by HTMX via inherited ``hx-boost`` on
 * ``#main-content`` from ``base_dashboard.html`` — this module only
 * deals with show/hide of the panel + the Reset button which has to
 * snap the ``<select name="archived">`` back to ``active`` and trigger
 * a fresh submit.
 *
 * Idempotency: we tag the bound buttons with a plain JS property
 * (``_custDetailFilterBound``), not a ``data-*`` attribute, because
 * HTMX hx-boost history snapshots clone the DOM via outerHTML —
 * datasets survive the round-trip but attached listeners do NOT.
 * A plain property is invisible to outerHTML so we re-bind correctly
 * after every back-button restore. Same trick as ``customers.js``.
 *
 * Also calls ``initCustomerList`` so the Pause / Activate / Deactivate
 * action icons rendered in ``page_actions`` (see
 * ``templates/office/customers/detail.html``) get the same status-change
 * UX (drawers + confirm modal) as on the customer list page. The init
 * is no-op for the parts of the list UI that don't exist on detail
 * (filter panel, customer search picker) — every internal helper
 * early-returns when its DOM hooks are missing.
 */

import { initCustomerList } from './customers.js';

function initFilterPanel() {
    var panel = document.getElementById('filter-panel');
    var toggleBtn = document.getElementById('toggle-filters-btn');
    var resetBtn = document.getElementById('reset-filters-btn');
    var chevron = document.getElementById('filter-chevron');
    var form = document.getElementById('worksitesFilterForm');

    // hx-boost-init.mdc §2 — self-detect the host tab. The ONLY tab whose
    // filter panel this module owns is Worksites (it carries
    // ``#worksitesFilterForm``). The Jobs tab now embeds the standalone
    // jobs-list partial, which shares the ``#toggle-filters-btn`` /
    // ``#filter-panel`` ids but is wired by ``jobs.js`` (``jobsList``
    // auto-init). Binding the toggle here too would attach a SECOND click
    // listener on the same button → it toggles twice per click → the
    // panel appears not to open. Early-return when the worksites form is
    // absent so jobs.js stays the sole owner on the Jobs tab.
    if (!form) return;

    if (toggleBtn && panel && !toggleBtn._custDetailFilterBound) {
        toggleBtn._custDetailFilterBound = true;
        toggleBtn.addEventListener('click', function () {
            var isHidden = panel.classList.toggle('hidden');
            if (chevron) chevron.classList.toggle('rotate-180', !isHidden);
        });
    }

    if (resetBtn && form && !resetBtn._custDetailResetBound) {
        resetBtn._custDetailResetBound = true;
        resetBtn.addEventListener('click', function () {
            var sel = form.querySelector('select[name="archived"]');
            if (!sel) return;
            // ``active`` is the default — resetting means snapping back
            // to it and letting the ``change`` trigger reload the page.
            if (sel.value !== 'active') {
                sel.value = 'active';
                sel.dispatchEvent(new Event('change', { bubbles: true }));
            }
        });
    }
}

export function initCustomerDetail(options = {}) {
    initFilterPanel();

    // Wire the customer-list status-change machinery (Pause / Activate /
    // Deactivate / Delete drawers + confirm modal) so the action icons
    // in the page header behave identically to those on the list page.
    initCustomerList({
        hasActiveFilters: false,
        urls: {},
        translations: options.translations || {},
    });

    // NOTE: tab navigation (?tab=worksites → ?tab=jobs → back) re-renders
    // ``#main-content`` and the wrapping ``[data-auto-init="customerDetail"]``
    // node, so app.js's ``_runAutoInit(container, true)`` on
    // ``htmx:afterSwap`` re-fires ``autoInitCustomerDetail`` for us — no
    // need for a separate global ``htmx:afterSettle`` hook here. A previous
    // version did register such a hook, but it stayed bound for the whole
    // SPA lifetime and matched ``#toggle-filters-btn`` on EVERY list page
    // (jobs, customers, visits, …), attaching a second click listener that
    // toggled the filter panel a second time on the same click — net result
    // "nothing happens" once you'd visited a customer detail page in the
    // session. See ``hx-boost-init.mdc`` for the canonical pattern.
}

export function autoInitCustomerDetail() {
    var root = document.querySelector('[data-auto-init="customerDetail"]');
    if (!root) return;
    var ds = root.dataset;
    initCustomerDetail({
        translations: {
            activate: ds.trActivate || 'Activate',
            activateMessage: ds.trActivateMessage || 'This will set the customer status to Active.',
            yesActivate: ds.trYesActivate || 'Yes, Activate',
            pauseTitle: ds.trPauseTitle || 'Pause Service',
            deactivateTitle: ds.trDeactivateTitle || 'Deactivate Customer',
            errorLoadingPauseForm: ds.trErrorPause || 'Error loading pause form',
            errorLoadingDeactivateForm: ds.trErrorDeactivate || 'Error loading deactivate form',
            errorOccurred: ds.trErrorOccurred || 'An error occurred. Please try again.',
            close: ds.trClose || 'Close',
        },
    });
}
